import {memo, useCallback, useEffect, useMemo} from 'react'

import {useBuildTypeData} from '../../../../../hooks/useEntity'
import {restApi} from '../../../../../services/rest'
import {stringifyId, toVcsRootId} from '../../../../../types'
import {subscribeOnBuildTypeEvents} from '../../../../../utils/subscriber'
import {BUILD_CHANGES_LOADED} from '../../../../../utils/subscriptionEvents'
import {UserProperties} from '../../../../../utils/userProperties'
import ContentPanel from '../../../../common/ContentPanel/ContentPanel'
import Grid from '../../../../common/Grid/Grid'
import {useSequenceLoaderReadyHandler} from '../../../../common/SequenceLoader/SequenceLoader.hooks'

import ChangesRepository from './ChangesRepository/ChangesRepository'
import {getBuildChangesRevisionsArg} from './ChangesRevisions.rest'
import {
  getBuildRevisionIdsByVcsRootInstanceId,
  getBuildsRevisionIdsByVcsRootInstanceId,
} from './ChangesRevisions.selectors'
import type {Props} from './ChangesRevisions.types'
import {VcsRootGroup} from './ChangesRevisions.types'
import {
  getCurrentBuildRevisionsLocator,
  getDependencyBuildRevisionsLocator,
  sortByVcsRootName,
} from './ChangesRevisions.utils'

import styles from './ChangesRevisions.css'

const options = {
  withSettingsRevisions: true,
}

function ChangesRevisions({buildId}: Props) {
  const buildTypeInternalId = useBuildTypeData({buildId}, buildType => buildType?.internalId)

  const [fetchDependencyBuildsRevisions, {dependencyBuildRevisionsData}] =
    restApi.endpoints.getAllBuildsNormalized.useLazyQuery({
      selectFromResult: ({data}) => ({dependencyBuildRevisionsData: data}),
    })
  const [fetchCurrentBuildRevisions, {currentBuildRevisionsData}] =
    restApi.endpoints.getAllBuildsNormalized.useLazyQuery({
      selectFromResult: ({data}) => ({
        currentBuildRevisionsData: data,
      }),
    })
  useSequenceLoaderReadyHandler(
    dependencyBuildRevisionsData != null && currentBuildRevisionsData != null,
  )

  const fetch = useCallback(() => {
    const currentBuildRevisionsLocator = getCurrentBuildRevisionsLocator(buildId)
    const dependencyBuildRevisionsLocator = getDependencyBuildRevisionsLocator(buildId)

    fetchCurrentBuildRevisions(getBuildChangesRevisionsArg(currentBuildRevisionsLocator, options))
    fetchDependencyBuildsRevisions(getBuildChangesRevisionsArg(dependencyBuildRevisionsLocator))
  }, [fetchDependencyBuildsRevisions, fetchCurrentBuildRevisions, buildId])

  useEffect(() => {
    if (buildTypeInternalId != null) {
      return subscribeOnBuildTypeEvents(buildTypeInternalId, [BUILD_CHANGES_LOADED], fetch)
    } else {
      fetch()
      return undefined
    }
  }, [buildTypeInternalId, fetch])

  const currentBuildRevisionIdsByVcsRootInstanceId = useMemo(() => {
    const {builds, revisions} = currentBuildRevisionsData?.entities ?? {}
    const build = builds?.[buildId]
    return getBuildRevisionIdsByVcsRootInstanceId(build?.revisions?.revision ?? [], revisions ?? {})
  }, [currentBuildRevisionsData, buildId])
  const currentBuildVCSRootInstanceIds = useMemo(() => {
    const {vcsRoots, vcsRootInstances} = currentBuildRevisionsData?.entities ?? {}
    return Object.keys(currentBuildRevisionIdsByVcsRootInstanceId)
      .map(toVcsRootId)
      .sort(sortByVcsRootName(vcsRootInstances ?? {}, vcsRoots ?? {}))
  }, [currentBuildRevisionsData, currentBuildRevisionIdsByVcsRootInstanceId])
  const settingsRevisionId =
    currentBuildRevisionsData?.entities?.builds?.[buildId]?.versionedSettingsRevision
  const settingsRevisionIds = useMemo(
    () => (settingsRevisionId != null ? [settingsRevisionId] : null),
    [settingsRevisionId],
  )
  const currentBuildSettingsVcsRootInstanceId =
    currentBuildRevisionsData?.entities.revisions?.[settingsRevisionId!]?.['vcs-root-instance']
  const dependencyBuildsRevisionIdsByVcsRootInstanceId = useMemo(() => {
    const {builds, revisions, vcsRootInstances} = dependencyBuildRevisionsData?.entities ?? {}

    return getBuildsRevisionIdsByVcsRootInstanceId(
      dependencyBuildRevisionsData?.result ?? [],
      revisions ?? {},
      vcsRootInstances ?? {},
      builds ?? {},
    )
  }, [dependencyBuildRevisionsData])
  const dependencyBuildsVCSRootInstanceIds = useMemo(() => {
    const {vcsRoots, vcsRootInstances} = dependencyBuildRevisionsData?.entities ?? {}

    return Object.keys(dependencyBuildsRevisionIdsByVcsRootInstanceId)
      .map(toVcsRootId)
      .sort(sortByVcsRootName(vcsRootInstances ?? {}, vcsRoots ?? {}))
  }, [dependencyBuildRevisionsData, dependencyBuildsRevisionIdsByVcsRootInstanceId])

  if (
    currentBuildSettingsVcsRootInstanceId == null &&
    currentBuildVCSRootInstanceIds.length === 0 &&
    dependencyBuildsVCSRootInstanceIds.length === 0
  ) {
    return null
  }

  return (
    <ContentPanel
      data-test-changes-revisions
      panelType={UserProperties.EXPAND_REVISIONS_SECTION}
      heading="Revisions"
      content={
        <Grid className={styles.grid}>
          {currentBuildVCSRootInstanceIds.map(vcsRootInstanceId => (
            <ChangesRepository
              key={stringifyId(vcsRootInstanceId)}
              vcsRootInstanceId={vcsRootInstanceId}
              buildId={buildId}
              revisionIds={currentBuildRevisionIdsByVcsRootInstanceId[vcsRootInstanceId]}
            />
          ))}
          {settingsRevisionId != null && (
            <ChangesRepository
              vcsRootInstanceId={currentBuildSettingsVcsRootInstanceId}
              revisionIds={settingsRevisionIds}
              vcsGroup={VcsRootGroup.SETTINGS}
              buildId={buildId}
            />
          )}
          {dependencyBuildsVCSRootInstanceIds.map(vcsRootInstanceId => (
            <ChangesRepository
              key={stringifyId(vcsRootInstanceId)}
              vcsRootInstanceId={vcsRootInstanceId}
              revisionIds={dependencyBuildsRevisionIdsByVcsRootInstanceId[vcsRootInstanceId]}
              vcsGroup={VcsRootGroup.DEPENDENCIES}
            />
          ))}
        </Grid>
      }
      withBorder={false}
      expandedByDefault={false}
    />
  )
}

export default memo<Props>(ChangesRevisions)
